import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile, deleteFile } from '../../firebase/storageService';
import { cache } from '../../utils/cacheManager';
import {
  FaUser,
  FaCamera,
  FaTrash,
  FaSave,
  FaUndo,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaVenusMars,
  FaBell,
  FaShieldAlt,
  FaEdit,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

const Profile: React.FC = () => {
  const { currentUser, userProfile, updateUserProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    gender: 'prefer-not-to-say' as 'male' | 'female' | 'other' | 'prefer-not-to-say',
    marketingEmails: false,
  });

  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        phone: userProfile.phone || '',
        dateOfBirth: userProfile.dateOfBirth || '',
        gender: (userProfile.gender as any) || 'prefer-not-to-say',
        marketingEmails: !!userProfile.marketingEmails,
      });
      const photo = (userProfile as any).profilePhoto || (userProfile as any).profileImage || null;
      setProfilePhotoPreview(photo);
    }
  }, [currentUser, userProfile, navigate]);

  const handleInputChange = (field: keyof typeof formData) => (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = e.target.type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Uploading profile photo...');
    
    try {
      const resp = await uploadFile(file, `user-profiles/${currentUser.uid}`);
      if (resp.success) {
        const updates = {
          profilePhoto: resp.url,
          profilePhotoPath: resp.path
        };

        const result = await updateUserProfile(updates as any);
        if (result.success) {
          setProfilePhotoPreview(resp.url || null);
          toast.success('Profile photo updated successfully!', { 
            id: toastId,
            icon: '📸'
          });
          // Invalidate cached user entries so other views see the update
          try { if (currentUser) { cache.invalidate(`user.${currentUser.uid}`); cache.invalidatePattern(/^users/); } } catch (e) {}
          await refreshUserProfile();
        } else {
          toast.error(result.error || 'Failed to update profile', { id: toastId });
        }
      } else {
        toast.error(resp.error || 'Upload failed', { id: toastId });
      }
    } catch (error: any) {
      toast.error(error.message || 'Upload failed', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!currentUser || !userProfile) return;
    
    const hasPhotoPath = !!(userProfile as any).profilePhotoPath;
    const hasPhoto = !!(userProfile as any).profilePhoto;
    
    if (!hasPhotoPath && !hasPhoto) {
      toast.error('No profile photo to delete');
      return;
    }

    if (!window.confirm('Are you sure you want to delete your profile photo?')) return;
    
    const toastId = toast.loading('Deleting profile photo...');
    try {
      // Delete from storage if path exists
      if (hasPhotoPath) {
        const del = await deleteFile(((userProfile as any).profilePhotoPath) as string);
        if (!del.success) {
          console.warn('Failed to delete file from storage:', del.error);
        }
      }

      // Clear photo fields in profile
      const result = await updateUserProfile({ 
        profilePhoto: '', 
        profilePhotoPath: '' 
      } as any);
      
      if (result.success) {
        setProfilePhotoPreview(null);
        toast.success('Profile photo removed successfully!', { 
          id: toastId,
          icon: '🗑️'
        });
        try { if (currentUser) { cache.invalidate(`user.${currentUser.uid}`); cache.invalidatePattern(/^users/); } } catch (e) {}
        await refreshUserProfile();
      } else {
        toast.error(result.error || 'Failed to update profile', { id: toastId });
      }
    } catch (error: any) {
      toast.error(error.message || 'Delete failed', { id: toastId });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setSaving(true);
    const toastId = toast.loading('Saving profile changes...');
    
    try {
      const updates: any = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        marketingEmails: formData.marketingEmails,
        updatedAt: new Date().toISOString()
      };

      // Only include dateOfBirth if user provided a value (avoid sending undefined)
      if (formData.dateOfBirth && formData.dateOfBirth.trim() !== '') {
        updates.dateOfBirth = formData.dateOfBirth;
      }

      const result = await updateUserProfile(updates as any);
      if (result.success) {
        toast.success('Profile updated successfully!', { 
          id: toastId,
          icon: '✅'
        });
        try { if (currentUser) { cache.invalidate(`user.${currentUser.uid}`); cache.invalidatePattern(/^users/); } } catch (e) {}
        await refreshUserProfile();
      } else {
        toast.error(result.error || 'Failed to save profile', { id: toastId });
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save profile', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        phone: userProfile.phone || '',
        dateOfBirth: userProfile.dateOfBirth || '',
        gender: (userProfile.gender as any) || 'prefer-not-to-say',
        marketingEmails: !!userProfile.marketingEmails,
      });
      toast('Changes reset', { icon: '↩️' });
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-50 to-primary-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
          <FaExclamationCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to view your profile</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-primary-700 transition-all transform hover:-translate-y-0.5"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const displayName = formData.firstName && formData.lastName 
    ? `${formData.firstName} ${formData.lastName}`
    : currentUser.displayName || 'User';

  const emailVerified = currentUser.emailVerified;
  const formatDate = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const parseCreatedAt = (createdAt: any) => {
    if (!createdAt) return null;
    // Firestore Timestamp
    if (typeof createdAt === 'object') {
      if (typeof createdAt.toDate === 'function') {
        try { return createdAt.toDate(); } catch { /* fallthrough */ }
      }
      if (typeof createdAt.seconds === 'number') {
        return new Date(createdAt.seconds * 1000);
      }
    }
    // string or number
    if (typeof createdAt === 'string' || typeof createdAt === 'number') {
      const d = new Date(createdAt as any);
      if (!isNaN(d.getTime())) return d;
    }
    return null;
  };

  const createdAtDate = parseCreatedAt(userProfile?.createdAt);
  const userSince = createdAtDate ? formatDate(createdAtDate) : 'Recently';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-2">Manage your personal information and preferences</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/3">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="relative">
                {/* Profile Photo */}
                <div className="flex flex-col items-center mb-6">
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
                      {profilePhotoPreview ? (
                        <img 
                          src={profilePhotoPreview} 
                          alt={displayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                          <span className="text-4xl font-bold text-white">
                            {displayName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Photo Upload Overlay */}
                    <label 
                      htmlFor="profile-photo-upload"
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <FaCamera className="h-6 w-6 text-white" />
                      <input
                        id="profile-photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Photo Actions */}
                    <div className="flex gap-3 mt-4">
                    <label 
                      htmlFor="profile-photo-upload"
                      className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-600 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-700 transition-all ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <FaCamera />
                      {uploading ? 'Uploading...' : 'Change Photo'}
                    </label>
                    
                    {profilePhotoPreview && (
                      <button
                        onClick={handleDeletePhoto}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                      >
                        <FaTrash />
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* User Info */}
                  <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">{displayName}</h2>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <FaEnvelope className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-600">{currentUser.email}</p>
                    {emailVerified && (
                      <FaCheckCircle className="h-4 w-4 text-green-500" title="Email Verified" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Member since {userSince}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-primary-50 rounded-lg">
                    <p className="text-2xl font-bold text-primary-600">{(userProfile as any)?.loginCount || 0}</p>
                    <p className="text-xs text-gray-600">Logins</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {userProfile?.isActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-xs text-gray-600">Status</p>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="space-y-1">
                <button
                  onClick={() => setActiveTab('personal')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'personal'
                      ? 'bg-gradient-to-r from-primary-50 to-primary-50 text-primary-700 border-l-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FaUser className="h-5 w-5" />
                  <span className="font-medium">Personal Info</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('preferences')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'preferences'
                      ? 'bg-gradient-to-r from-primary-50 to-primary-50 text-primary-700 border-l-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FaBell className="h-5 w-5" />
                  <span className="font-medium">Preferences</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'security'
                      ? 'bg-gradient-to-r from-primary-50 to-primary-50 text-primary-700 border-l-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FaShieldAlt className="h-5 w-5" />
                  <span className="font-medium">Security</span>
                </button>
              </div>
            </div>

            {/* Account Status */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-600 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="font-bold text-lg mb-2">Account Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-primary-100">Email Verification</span>
                  {emailVerified ? (
                    <span className="px-3 py-1 bg-green-500 bg-opacity-20 text-green-200 rounded-full text-xs font-medium">
                      Verified
                    </span>
                  ) : (
                    <button className="px-3 py-1 bg-yellow-500 bg-opacity-20 text-yellow-200 rounded-full text-xs font-medium hover:bg-opacity-30">
                      Verify Now
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-primary-100">Account Type</span>
                  <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-medium">
                    {userProfile?.role || 'User'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-2/3">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Form Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {activeTab === 'personal' && 'Personal Information'}
                  {activeTab === 'preferences' && 'Preferences'}
                  {activeTab === 'security' && 'Security Settings'}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {activeTab === 'personal' && 'Update your personal details and contact information'}
                  {activeTab === 'preferences' && 'Manage your notification and privacy preferences'}
                  {activeTab === 'security' && 'Change your password and security settings'}
                </p>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSave} className="p-6">
                {activeTab === 'personal' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={handleInputChange('firstName')}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter your first name"
                          />
                          <FaEdit className="absolute right-3 top-3.5 h-5 w-5 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={handleInputChange('lastName')}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                            placeholder="Enter your last name"
                          />
                          <FaEdit className="absolute right-3 top-3.5 h-5 w-5 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FaPhone className="inline h-4 w-4 mr-2 text-gray-400" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange('phone')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FaBirthdayCake className="inline h-4 w-4 mr-2 text-gray-400" />
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange('dateOfBirth')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FaVenusMars className="inline h-4 w-4 mr-2 text-gray-400" />
                          Gender
                        </label>
                        <select
                          value={formData.gender}
                          onChange={handleInputChange('gender')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                        >
                          <option value="prefer-not-to-say">Prefer not to say</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          {/* <option value="other">Other</option> */}
                        </select>
                      </div>
                    </div>

                    <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
                      <h3 className="font-medium text-primary-800 mb-2">Contact Information</h3>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm text-primary-700">Email Address</p>
                          <p className="font-medium">{currentUser.email}</p>
                        </div>
                        {!emailVerified && (
                          <button
                            type="button"
                            className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-200 transition-colors"
                          >
                            Verify Email
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'preferences' && (
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-medium text-gray-900 mb-4">Email Preferences</h3>
                      
                      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-100 rounded-lg">
                            <FaBell className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Marketing Emails</h4>
                            <p className="text-sm text-gray-600">Receive promotional offers and updates</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.marketingEmails}
                            onChange={handleInputChange('marketingEmails')}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-medium text-gray-900 mb-4">Notification Settings</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                          <span className="text-gray-900">Push Notifications</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                          <span className="text-gray-900">Email Notifications</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'security' && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-primary-50 to-primary-50 rounded-xl p-6 border border-primary-200">
                      <h3 className="font-bold text-gray-900 mb-2">Change Password</h3>
                      <p className="text-gray-600 mb-4 text-sm">Secure your account with a new password</p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter current password"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter new password"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            Must be at least 8 characters with uppercase, lowercase, and numbers
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Confirm new password"
                          />
                        </div>
                        
                        <button
                          type="button"
                          className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-primary-700 transition-all transform hover:-translate-y-0.5"
                        >
                          Update Password
                        </button>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <h3 className="font-bold text-gray-900 mb-2">Danger Zone</h3>
                      <p className="text-gray-600 mb-4 text-sm">Irreversible and destructive actions</p>
                      
                      <div className="space-y-3">
                        <button
                          type="button"
                          className="w-full py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                          onClick={() => {
                            if (window.confirm('Are you sure? This will log you out from all devices.')) {
                              // Implement logout all sessions
                            }
                          }}
                        >
                          Log Out All Sessions
                        </button>
                        
                        <button
                          type="button"
                          className="w-full py-3 border-2 border-red-600 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-colors"
                          onClick={() => {
                            if (window.confirm('This will permanently delete your account. This action cannot be undone.')) {
                              // Implement account deletion
                            }
                          }}
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6 mt-8 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-primary-600 to-primary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-primary-700 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    <FaSave />
                    {saving ? 'Saving Changes...' : 'Save Changes'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-6 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    <FaUndo />
                    Reset Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;